import { useState } from "react";
import { money, thumb, AddToCartButton, Collage, PreviewDisclaimer } from "./shared";

/* Picks the fixed bundle best suited for the PDP upsell mock and the
   child item to feature on it. Prefers the deepest discount (the upsell
   pitch is savings) and an item with a real image. */
export function pickUpsell(bundles) {
  const candidates = bundles.filter(
    (bundle) => bundle.type === "SimpleBundle" && bundle.items.some((item) => item.image_url)
  );
  if (candidates.length === 0) return null;
  const bundle = [...candidates].sort(
    (a, b) => (b.discount_percentage || 0) - (a.discount_percentage || 0)
  )[0];
  const item = bundle.items.find((i) => i.image_url);
  return { bundle, item };
}

/* A mock storefront product page for one bundle child item, with the
   Simple Bundles PDP upsell widget underneath the buy box. Mirrors the
   real theme extension's copy: "Make it a bundle", "Includes", "+ Add". */
export default function UpsellPdp({ bundle, item }) {
  const variants = item.variants || [];
  const hasVariants = variants.length > 1;
  const [selected, setSelected] = useState(
    variants.find((v) => v.id === item.variant_id) || variants[0] || null
  );
  const price = selected?.price ?? item.price;
  const image = selected?.image_url || item.image_url;
  const slug = item.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

  return (
    <article className="card overflow-hidden">
      {/* Mock browser chrome so this reads as "your product page", not another card */}
      <div className="flex items-center gap-1.5 border-b border-ink/5 bg-paper px-5 py-3">
        <span className="h-2.5 w-2.5 rounded-full bg-ink/10" aria-hidden="true"></span>
        <span className="h-2.5 w-2.5 rounded-full bg-ink/10" aria-hidden="true"></span>
        <span className="h-2.5 w-2.5 rounded-full bg-ink/10" aria-hidden="true"></span>
        <span className="ml-3 truncate rounded-full bg-white px-3.5 py-1 text-xs text-ink-soft">
          yourstore.com/products/{slug}
        </span>
      </div>

      <div className="grid gap-8 p-6 sm:p-10 lg:grid-cols-2 lg:gap-12">
        <div className="overflow-hidden rounded-xl bg-white">
          <img
            src={thumb(image, 1000)}
            alt={item.title}
            loading="lazy"
            className="aspect-square h-full w-full object-cover"
          />
        </div>

        <div className="flex flex-col">
          {(item.vendor || item.product_type) && (
            <p className="kicker mb-2">{item.vendor || item.product_type}</p>
          )}
          <h3 className="display text-3xl">{item.title}</h3>
          <div className="mt-3 flex items-baseline gap-2.5">
            <span className="font-display text-2xl font-bold">{money(price)}</span>
            {item.compare_at_price && item.compare_at_price > price && (
              <span className="text-sm text-ink/40 line-through">{money(item.compare_at_price)}</span>
            )}
          </div>
          {item.description && (
            <p className="mt-3 text-sm leading-relaxed text-ink-soft">{item.description}</p>
          )}

          {hasVariants && (
            <div className="mt-5">
              <p className="text-xs font-semibold uppercase tracking-wider text-ink-soft">
                {item.option_names[0] || "Options"}
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {variants.slice(0, 6).map((variant) => (
                  <button
                    key={variant.id}
                    type="button"
                    onClick={() => setSelected(variant)}
                    className={`rounded-full border px-3.5 py-1.5 text-sm font-medium transition ${
                      selected?.id === variant.id
                        ? "border-ink bg-ink text-white"
                        : "border-ink/15 bg-white hover:border-ink/40"
                    }`}
                  >
                    {variant.title || "Default"}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="mt-6">
            <AddToCartButton label="Add to cart" />
          </div>

          <div className="mt-8 rounded-2xl border border-ink/10 bg-sky-wash/60 p-5">
            <p className="font-display text-lg font-bold">Make it a bundle</p>
            <p className="mt-0.5 text-sm text-ink-soft">
              Save when you bundle this with our most-loved sets
            </p>

            <div className="mt-4 flex gap-4">
              {/* The items side by side, same collage treatment as the bundle
                  cards — reads as "the bundle", no single-product close-up */}
              <div className="relative w-28 shrink-0">
                <Collage urls={bundle.items.map((i) => i.image_url)} size="h-28" />
                {bundle.discount_percentage > 0 && (
                  <span className="absolute -left-2 -top-2 rounded-full bg-[#D92D20] px-2 py-0.5 text-[11px] font-bold text-white">
                    {Math.round(bundle.discount_percentage)}% OFF
                  </span>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-semibold">{bundle.title}</p>
                <details className="mt-1 text-sm text-ink-soft">
                  <summary className="cursor-pointer select-none font-medium">Includes</summary>
                  <ul className="mt-1.5 space-y-1">
                    {bundle.items.map((bundleItem) => (
                      <li key={bundleItem.id} className="truncate">
                        {bundleItem.quantity} × {bundleItem.title}
                      </li>
                    ))}
                  </ul>
                </details>
                <div className="mt-2 flex items-baseline gap-2">
                  <span className="font-display text-lg font-bold">{money(bundle.price)}</span>
                  {bundle.compare_at_price && (
                    <span className="text-sm text-ink/40 line-through">{money(bundle.compare_at_price)}</span>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-4">
              <UpsellAddButton />
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}

function UpsellAddButton() {
  const [added, setAdded] = useState(false);

  const click = () => {
    if (added) return;
    setAdded(true);
    setTimeout(() => setAdded(false), 2600);
  };

  return (
    <div>
      <button
        type="button"
        onClick={click}
        className={`btn-primary w-full !py-2.5 text-sm transition-colors ${added ? "!bg-sky-deep" : ""}`}
      >
        {added ? "Bundle added ✓" : "+ Add"}
      </button>
      {added && <PreviewDisclaimer />}
    </div>
  );
}
